import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag } from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import { formatPrice } from '../utils/helpers';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Cart = () => {
  const { cart, updateCartItem, removeFromCart, clearCart, fetchCart } = useApp();
  const [cartProducts, setCartProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchCart(); }, []);

  useEffect(() => {
    const fetchCartProducts = async () => {
      if (cart.items.length === 0) { setCartProducts([]); setLoading(false); return; }
      try {
        setLoading(true);
        const responses = await Promise.all(cart.items.map(item => axios.get(`${API}/products/${item.product_id}`)));
        setCartProducts(responses.map((res, i) => ({ ...res.data, quantity: cart.items[i].quantity })));
      } catch (error) { console.error('Error fetching cart products:', error); }
      finally { setLoading(false); }
    };
    fetchCartProducts();
  }, [cart.items]);

  const updateQuantity = async (productId, newQuantity) => { if (newQuantity >= 1) await updateCartItem(productId, newQuantity); };
  const calculateTotal = () => cartProducts.reduce((t, p) => t + p.price * p.quantity, 0);

  if (loading) return (
    <div className="min-h-screen bg-[#FEFCF9] flex items-center justify-center">
      <div className="text-center">
        <div className="inline-block w-10 h-10 border-2 border-[#E8DCC8] border-t-[#C4A265] rounded-full animate-spin mb-4"></div>
        <p className="text-[#7A6B5D]">Loading cart...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FEFCF9] py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-serif text-[#3E2723] mb-2">Shopping Cart</h1>
        <div className="w-12 h-px bg-[#C4A265] mb-8"></div>

        {cartProducts.length === 0 ? (
          <div className="bg-[#FEFCF9] border border-[#E8DCC8] p-12 text-center">
            <ShoppingBag className="w-16 h-16 text-[#E8DCC8] mx-auto mb-4" />
            <h2 className="text-2xl font-serif text-[#3E2723] mb-2">Your cart is empty</h2>
            <p className="text-[#7A6B5D] mb-6">Discover our exquisite collection of handcrafted products</p>
            <Link to="/shop" className="inline-block bg-[#3E2723] text-[#FEFCF9] px-8 py-3 font-semibold hover:bg-[#C4A265] transition-colors tracking-wider uppercase text-sm">Start Shopping</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-4">
              {cartProducts.map((product) => (
                <div key={product.id} className="bg-[#FEFCF9] border border-[#E8DCC8] p-6 flex flex-col sm:flex-row gap-6" data-testid={`cart-item-${product.id}`}>
                  <Link to={`/product/${product.id}`} className="flex-shrink-0">
                    <img src={product.images[0] || 'https://via.placeholder.com/200'} alt={product.name} className="w-full sm:w-32 h-32 object-cover" />
                  </Link>
                  <div className="flex-1">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <Link to={`/product/${product.id}`} className="text-lg font-serif text-[#3E2723] hover:text-[#C4A265] transition-colors">{product.name}</Link>
                        <p className="text-sm text-[#C4A265] mt-1 tracking-wider uppercase">{product.craft_type}</p>
                      </div>
                      <button onClick={() => removeFromCart(product.id)} className="p-2 text-red-400 hover:bg-red-50 transition-colors" data-testid={`remove-item-${product.id}`}>
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-4">
                      <div className="flex items-center space-x-3">
                        <span className="text-sm text-[#7A6B5D] tracking-wider uppercase">Qty:</span>
                        <div className="flex items-center border border-[#E8DCC8]">
                          <button onClick={() => updateQuantity(product.id, product.quantity - 1)} disabled={product.quantity <= 1} className="px-3 py-1 hover:bg-[#F5EFE6] disabled:opacity-50 transition-colors" data-testid={`decrease-quantity-${product.id}`}><Minus className="w-4 h-4 text-[#3E2723]" /></button>
                          <span className="px-4 py-1 font-medium text-[#3E2723]" data-testid={`quantity-${product.id}`}>{product.quantity}</span>
                          <button onClick={() => updateQuantity(product.id, product.quantity + 1)} disabled={product.quantity >= product.stock} className="px-3 py-1 hover:bg-[#F5EFE6] disabled:opacity-50 transition-colors" data-testid={`increase-quantity-${product.id}`}><Plus className="w-4 h-4 text-[#3E2723]" /></button>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-[#7A6B5D]">Price</p>
                        <p className="text-xl font-bold text-[#3E2723]">{formatPrice(product.price * product.quantity)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              <button onClick={clearCart} className="text-red-400 hover:text-red-500 font-medium text-sm" data-testid="clear-cart-button">Clear Cart</button>
            </div>

            <div className="lg:col-span-1">
              <div className="bg-[#FEFCF9] border border-[#E8DCC8] p-6 sticky top-24">
                <h2 className="text-2xl font-serif text-[#3E2723] mb-6">Order Summary</h2>
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-[#7A6B5D]"><span>Subtotal ({cartProducts.reduce((s, p) => s + p.quantity, 0)} items)</span><span>{formatPrice(calculateTotal())}</span></div>
                  <div className="flex justify-between text-[#7A6B5D]"><span>Shipping</span><span>{calculateTotal() >= 15000 ? 'FREE' : formatPrice(500)}</span></div>
                  <div className="border-t border-[#E8DCC8] pt-3">
                    <div className="flex justify-between text-lg font-bold text-[#3E2723]"><span>Total</span><span>{formatPrice(calculateTotal() + (calculateTotal() >= 15000 ? 0 : 500))}</span></div>
                  </div>
                </div>
                {calculateTotal() < 15000 && (
                  <div className="bg-[#F5EFE6] border border-[#E8DCC8] text-[#7A6B5D] p-3 text-sm mb-4">Add {formatPrice(15000 - calculateTotal())} more for free shipping!</div>
                )}
                <Link to="/checkout" className="block w-full bg-[#3E2723] text-[#FEFCF9] text-center py-3.5 font-semibold hover:bg-[#C4A265] transition-colors tracking-wider uppercase text-sm" data-testid="proceed-to-checkout-button">Proceed to Checkout</Link>
                <Link to="/shop" className="block w-full text-center text-[#C4A265] py-3 mt-3 font-medium hover:text-[#A0822E] transition-colors text-sm tracking-wider uppercase">Continue Shopping</Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cart;
